import React, { useState, useEffect } from 'react';
import { Card, Button, Badge } from '../common';
import {
  UserPlus,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  Search,
  User,
  Shield,
  AlertCircle,
  RotateCcw,
  CloudUpload,
} from 'lucide-react';
import type { Inspector } from '../../types';
import {
  getAllInspectors,
  saveInspector,
  deleteInspector,
  DEFAULT_INSPECTORS,
} from '../../db';
import { broadcastSystemData } from '../../services/config/cloudConfigService';

export const InspectorManager: React.FC = () => {
  const [inspectors, setInspectors] = useState<Inspector[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Modal / Form state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingInspector, setEditingInspector] = useState<Inspector | null>(null);
  const [formName, setFormName] = useState<string>('');
  const [formRole, setFormRole] = useState<string>('');
  const [formActive, setFormActive] = useState<boolean>(true);
  const [formError, setFormError] = useState<string>('');
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string>('');
  const [isBroadcasting, setIsBroadcasting] = useState<boolean>(false);

  // Delete confirmation
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const list = await getAllInspectors();
      setInspectors(list);
    } catch (err) {
      console.error('Error loading inspectors:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenAddModal = () => {
    setEditingInspector(null);
    setFormName('');
    setFormRole('');
    setFormActive(true);
    setFormError('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (inspector: Inspector) => {
    setEditingInspector(inspector);
    setFormName(inspector.name);
    setFormRole(inspector.role || '');
    setFormActive(inspector.active);
    setFormError('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingInspector(null);
    setFormName('');
    setFormRole('');
    setFormActive(true);
    setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = formName.trim();
    if (!trimmedName) {
      setFormError('กรุณาระบุชื่อ-นามสกุลของผู้ตรวจสอบ');
      return;
    }

    try {
      const inspectorData: Inspector = {
        id: editingInspector ? editingInspector.id : `insp-${Date.now()}`,
        name: trimmedName,
        role: formRole.trim() || 'ช่างซ่อมบำรุง',
        active: formActive,
      };

      const updated = await saveInspector(inspectorData);
      setInspectors(updated);
      handleCloseModal();

      // Broadcast changes to all devices via Server & Google Sheets
      broadcastSystemData({ reason: `บันทึกผู้ตรวจเช็ก ${trimmedName}` }).catch(() => {});

      setSaveSuccessMsg(
        editingInspector
          ? `อัปเดตและกระจายข้อมูล "${trimmedName}" ไปยังทุกเครื่องเรียบร้อยแล้ว`
          : `เพิ่มและกระจายผู้ตรวจสอบ "${trimmedName}" ไปยังทุกเครื่องเรียบร้อยแล้ว`
      );
      setTimeout(() => setSaveSuccessMsg(''), 4500);
    } catch (err: any) {
      setFormError(err.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    }
  };

  const handleToggleStatus = async (inspector: Inspector) => {
    try {
      const updated = await saveInspector({
        ...inspector,
        active: !inspector.active,
      });
      setInspectors(updated);
      broadcastSystemData({ reason: `สลับสถานะ ${inspector.name}` }).catch(() => {});
    } catch (err) {
      console.error('Error toggling inspector status:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const updated = await deleteInspector(id);
      setInspectors(updated);
      setDeletingId(null);
      broadcastSystemData({ reason: 'ลบรายชื่อผู้ตรวจเช็ก' }).catch(() => {});
      setSaveSuccessMsg('ลบและกระจายการตั้งค่ารายชื่อผู้ตรวจสอบเรียบร้อยแล้ว');
      setTimeout(() => setSaveSuccessMsg(''), 3500);
    } catch (err: any) {
      alert(`ไม่สามารถลบข้อมูลได้: ${err.message}`);
    }
  };

  const handleResetDefaults = async () => {
    if (window.confirm('คุณต้องการรีเซ็ตรายชื่อผู้ตรวจสอบเป็นค่าเริ่มต้นโรงงานใช่หรือไม่?')) {
      for (const def of DEFAULT_INSPECTORS) {
        await saveInspector(def);
      }
      await loadData();
      broadcastSystemData({ reason: 'รีเซ็ตรายชื่อผู้ตรวจเช็กเริ่มต้น' }).catch(() => {});
      setSaveSuccessMsg('รีเซ็ตและกระจายรายชื่อเริ่มต้นเรียบร้อยแล้ว');
      setTimeout(() => setSaveSuccessMsg(''), 3500);
    }
  };

  const handleManualBroadcast = async () => {
    setIsBroadcasting(true);
    try {
      const res = await broadcastSystemData({ reason: 'ซิงค์รายชื่อช่างไปยังทุกเครื่อง' });
      setSaveSuccessMsg(res.message || 'กระจายข้อมูลไปยังทุกเครื่องเรียบร้อยแล้ว');
      setTimeout(() => setSaveSuccessMsg(''), 5000);
    } catch (err: any) {
      alert(`กระจายข้อมูลไม่สำเร็จ: ${err.message || 'Unknown error'}`);
    } finally {
      setIsBroadcasting(false);
    }
  };

  const filteredInspectors = inspectors.filter((i) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      i.name.toLowerCase().includes(q) ||
      (i.role && i.role.toLowerCase().includes(q))
    );
  });

  const activeCount = inspectors.filter((i) => i.active).length;

  return (
    <div className="space-y-5">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 sm:p-5 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-900">
              จัดการรายชื่อผู้ตรวจสอบ (Inspector Management)
            </h2>
            <Badge variant="blue">{inspectors.length} ท่าน</Badge>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            รายชื่อช่างและเจ้าหน้าที่ที่เปิดใช้งาน (Active) จะปรากฏให้เลือกในเมนู Dropdown ของหน้าตรวจสอบ
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetDefaults}
            className="text-xs"
            title="รีเซ็ตเป็นค่าเริ่มต้น"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            ค่าเริ่มต้น
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleManualBroadcast}
            disabled={isBroadcasting}
            className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
            title="กระจายรายชื่อช่างไปยังทุกเครื่องทันที"
          >
            <CloudUpload className={`w-3.5 h-3.5 mr-1 ${isBroadcasting ? 'animate-bounce' : ''}`} />
            {isBroadcasting ? 'กำลังกระจาย...' : 'กระจายข้อมูลไปทุกเครื่อง'}
          </Button>

          <Button
            variant="primary"
            onClick={handleOpenAddModal}
            className="shadow-sm"
          >
            <UserPlus className="w-4 h-4 mr-1.5" />
            เพิ่มผู้ตรวจสอบใหม่
          </Button>
        </div>
      </div>

      {/* Success Notification Alert */}
      {saveSuccessMsg && (
        <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 px-4 py-3 rounded-xl text-sm flex items-center gap-2 shadow-sm animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{saveSuccessMsg}</span>
        </div>
      )}

      {/* Search & Stat Summary */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="ค้นหาชื่อ หรือแผนก/ตำแหน่ง..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3.5 py-2 text-xs bg-white border border-slate-300 rounded-lg text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[40px]"
          />
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-600 self-end sm:self-center">
          <span>พร้อมปฏิบัติงาน: <strong className="text-emerald-600">{activeCount}</strong> ท่าน</span>
          <span>•</span>
          <span>ปิดการใช้งาน: <strong className="text-slate-400">{inspectors.length - activeCount}</strong> ท่าน</span>
        </div>
      </div>

      {/* Inspectors Table / Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="bg-white p-4 rounded-xl border border-slate-200 animate-pulse h-24" />
          ))}
        </div>
      ) : filteredInspectors.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-500 space-y-2">
          <User className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="font-semibold text-sm">ไม่พบรายชื่อผู้ตรวจสอบ</p>
          <p className="text-xs text-slate-400">
            {searchQuery ? 'ลองเปลี่ยนคำค้นหา หรือกดล้างการค้นหา' : 'กดปุ่ม "เพิ่มผู้ตรวจสอบใหม่" ด้านบนเพื่อเพิ่มช่างคนแรก'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
          {filteredInspectors.map((inspector) => (
            <div
              key={inspector.id}
              className={`bg-white rounded-xl border p-4 shadow-sm transition-all flex flex-col justify-between ${
                inspector.active
                  ? 'border-slate-200 hover:border-blue-400 hover:shadow-md'
                  : 'border-slate-200/60 bg-slate-50/70 opacity-75'
              }`}
            >
              <div>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                        inspector.active
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-slate-200 text-slate-500'
                      }`}
                    >
                      <User className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 line-clamp-1">
                        {inspector.name}
                      </h3>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <Shield className="w-3 h-3 text-slate-400" />
                        {inspector.role || 'ช่างซ่อมบำรุง'}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleToggleStatus(inspector)}
                    className="shrink-0"
                    title={inspector.active ? 'คลิกเพื่อปิดใช้งาน' : 'คลิกเพื่อเปิดใช้งาน'}
                  >
                    {inspector.active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 cursor-pointer">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-200 text-slate-600 hover:bg-slate-300 cursor-pointer">
                        <XCircle className="w-3 h-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-1.5 pt-3 mt-3 border-t border-slate-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleOpenEditModal(inspector)}
                  className="text-xs text-slate-600 hover:text-blue-600 hover:bg-blue-50 px-2.5 py-1"
                >
                  <Edit2 className="w-3.5 h-3.5 mr-1" />
                  แก้ไข
                </Button>

                {deletingId === inspector.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleDelete(inspector.id)}
                      className="px-2 py-1 text-xs bg-rose-600 text-white rounded font-medium hover:bg-rose-700 cursor-pointer"
                    >
                      ยืนยันลบ
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeletingId(null)}
                      className="px-2 py-1 text-xs bg-slate-200 text-slate-700 rounded font-medium hover:bg-slate-300 cursor-pointer"
                    >
                      ยกเลิก
                    </button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeletingId(inspector.id)}
                    className="text-xs text-slate-400 hover:text-rose-600 hover:bg-rose-50 px-2.5 py-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-md w-full p-5 sm:p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                  <UserPlus className="w-4 h-4" />
                </div>
                <h3 className="font-bold text-base text-slate-900">
                  {editingInspector ? 'แก้ไขข้อมูลผู้ตรวจสอบ' : 'เพิ่มผู้ตรวจสอบใหม่'}
                </h3>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
              >
                ✕
              </button>
            </div>

            {formError && (
              <div className="bg-rose-50 border border-rose-300 text-rose-800 text-xs p-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                <span>{formError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  ชื่อ-นามสกุล ผู้ตรวจสอบ <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="เช่น นายช่าง สมเกียรติ มุ่งมั่น"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  แผนก / ตำแหน่ง
                </label>
                <input
                  type="text"
                  placeholder="เช่น ช่างซ่อมบำรุงกะเช้า, วิศวกรซ่อมบำรุง"
                  value={formRole}
                  onChange={(e) => setFormRole(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-xl">
                <div>
                  <span className="text-xs font-bold text-slate-800">สถานะเปิดใช้งาน (Active)</span>
                  <p className="text-[11px] text-slate-500">
                    เมื่อเปิดใช้งาน รายชื่อนี้จะแสดงใน Dropdown ให้ช่างเลือกตรวจเช็ก
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={formActive}
                    onChange={(e) => setFormActive(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className={`w-11 h-6 rounded-full transition-colors duration-200 p-0.5 flex items-center ${
                      formActive ? 'bg-emerald-500' : 'bg-slate-300'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                        formActive ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </div>
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                <Button variant="outline" type="button" onClick={handleCloseModal}>
                  ยกเลิก
                </Button>
                <Button variant="primary" type="submit">
                  {editingInspector ? 'บันทึกการแก้ไข' : 'เพิ่มผู้ตรวจสอบ'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
